/**
 * CompanyBankAccountsTab — F12 PR 2 pkt B.2.
 *
 * Zakładka Konta bankowe firmy — wrapper wokół `BankAccountsTabBase` (wspólna
 * implementacja z `BankAccountsDynamicTab`, patrz jej JSDoc dla pełnego opisu).
 * Ten plik niesie tylko różnice wariantu firmowego: backend
 * `/api/companies/:id/bank-accounts` (response `{ bankAccounts: [...] }`
 * wrapped), moduł helper-encji `company_bank_accounts`, i BRAK whitelist VAT MF
 * (`whitelist: null` — companies nie mają MF whitelist, to feature contractors-only).
 *
 * Public API: `{ companyId, testIdPrefix? }`.
 */

import { useMemo } from 'react';

import type { BankAccountRow } from './bank-accounts-columns';
import { BankAccountsTabBase, type BankAccountsTabConfig } from './BankAccountsTabBase';

export interface CompanyBankAccountsTabProps {
  companyId: string;
  /** Custom testid prefix (default: `bank-accounts`). */
  testIdPrefix?: string;
  /** Slugi kolumn ukrytych z layoutu (formatka bloku `company_bank_accounts_view`). */
  hiddenColumnIds?: readonly string[];
  /** Kolejność kolumn (slugi) z formatki bloku — drag. */
  columnOrder?: readonly string[];
}

export function CompanyBankAccountsTab({
  companyId,
  testIdPrefix = 'bank-accounts',
  hiddenColumnIds,
  columnOrder,
}: CompanyBankAccountsTabProps) {
  const config = useMemo<BankAccountsTabConfig>(
    () => ({
      moduleSlug: 'company_bank_accounts',
      apiBase: `/companies/${companyId}/bank-accounts`,
      queryKey: ['company-bank-accounts', companyId],
      // Backend zwraca `{ bankAccounts: [...] }` wrapped (różnica vs contractor
      // endpoint, który zwraca array bezpośrednio).
      unwrapList: (data) => (data as { bankAccounts: BankAccountRow[] }).bankAccounts,
      whitelist: null,
      ...(hiddenColumnIds ? { hiddenColumnIds } : {}),
      ...(columnOrder ? { columnOrder } : {}),
    }),
    [companyId, hiddenColumnIds, columnOrder],
  );

  return <BankAccountsTabBase config={config} testIdPrefix={testIdPrefix} />;
}
